import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2" }
const _hoisted_2 = { class: "font-medium text-gray-600" }
const _hoisted_3 = { class: "group relative" }
const _hoisted_4 = { class: "invisible absolute left-0 top-full z-10 mt-1 w-48 rounded bg-gray-800 px-2 py-1 text-xs text-white group-hover:visible" }
const _hoisted_5 = { class: "sm:col-span-12" }
const _hoisted_6 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_7 = { class: "mt-6 grid grid-cols-2 place-content-around gap-4 text-center" }

import { ref, Ref, computed, WritableComputedRef, PropType, onMounted } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    VueFlowConnectionModel,
    NumberModel,
} from '@/types';
import store from '@/store';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';
import { IconRobot } from '@tabler/icons-vue';
import { Handle, Position } from '@vue-flow/core';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { NumberService } from '@/services';

interface IVirtualAgentFormState {
    number: null | NumberModel;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TransferVirtualAgent',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props) {

const { t } = useI18n();

const props = __props;

const showForm = ref(false);
const numberOptions: Ref<NumberModel[]> = ref([]);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const formState: Ref<IVirtualAgentFormState> = ref({
    number: null,
});

const textValidation = t('connectFlow.node.transferVirtualAgent.validation');
const numberRequired = helpers.withMessage(textValidation, (value, state) => {
    return !!state.number;
});

const rules = computed<ValidationArgs<IVirtualAgentFormState>>(() => {
    const r: ValidationArgs<IVirtualAgentFormState> = {
        number: {
            numberRequired,
        },
    };
    return r;
});

const v$: Ref<Validation<ValidationArgs<IVirtualAgentFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferVirtualAgent.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    }
    return text;
});

const sourceHandles = computed(() => {
    const connections: VueFlowConnectionModel[] =
        store.getters['flow/' + ConnectFlowModelingGetters.ELEMENTS_CONNECTIONS];
    const handles = new Set<string>();
    connections
        .filter((c: VueFlowConnectionModel) => c.source === nodeWritable.value.id)
        .forEach((c) => {
            if (c.sourceHandle) {
                handles.add(c.sourceHandle);
            }
        });
    if (handles.size === 0) {
        handles.add('hangupWithoutTransfer');
    }
    return Array.from(handles);
});

const sourceOptionHandles = computed(() =>
    sourceHandles.value.filter((h) => h !== 'hangupWithoutTransfer'),
);

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}

async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value && formState.value.number) {
        nodeWritable.value.data.related = {
            value: formState.value.number.id.toString(),
            label: formState.value.number.number,
        };
        nodeWritable.value.data.virtualAgentNumber = formState.value.number.number;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(async () => {
    try {
        const response = await NumberService.getAll();
        numberOptions.value = response.data;
        if (nodeWritable.value.data.related?.value) {
            formState.value.number =
                numberOptions.value.find(
                    (n) => n.id.toString() === nodeWritable.value.data.related?.value,
                ) || null;
        }
        isFormCorrect.value = !!formState.value.number;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    } catch (error) {
        console.error('Error loading numbers:', error);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiNode = _resolveComponent("UiNode")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiRightPanel = _resolveComponent("UiRightPanel")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_UiNode, {
      nodeId: __props.node.id,
      title: _ctx.$t('connectFlow.node.transferVirtualAgent.title'),
      icon: _unref(IconRobot),
      showActions: "",
      onEditNode: _cache[0] || (_cache[0] = ($event: any) => (showForm.value = true)),
      onRemoveNode: removeNode,
      class: _normalizeClass(isFormCorrect.value ? '' : 'border-red-300 shadow-red-300')
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(nodeText.value), 1),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(QuestionMarkCircleIcon), { class: "h-5 w-5 cursor-help text-gray-400 hover:text-gray-600" }),
            _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('connectFlow.node.transferVirtualAgent.tooltip')), 1)
          ])
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sourceOptionHandles.value, (handleId, idx) => {
          return (_openBlock(), _createBlock(_unref(Handle), {
            key: handleId,
            id: handleId,
            type: "source",
            connectable: false,
            position: _unref(Position).Bottom,
            style: _normalizeStyle(`left: ${(idx + 1) * (100 / (sourceOptionHandles.value.length + 1))}%; bottom: -6px`)
          }, null, 8, ["id", "position", "style"]))
        }), 128)),
        _createVNode(_unref(Handle), {
          id: "hangupWithoutTransfer",
          type: "source",
          connectable: false,
          position: _unref(Position).Left,
          style: {"top":"auto","bottom":"30px"}
        }, null, 8, ["position"])
      ]),
      _: 1
    }, 8, ["nodeId", "title", "icon", "class"]),
    _createVNode(_component_UiRightPanel, {
      modelValue: showForm.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showForm).value = $event)),
      title: _ctx.$t('connectFlow.node.transferVirtualAgent.title'),
      icon: _unref(IconRobot)
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          autocomplete: "off",
          class: "flex flex-col gap-3",
          onSubmit: _withModifiers(onSubmit, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('connectFlow.node.transferVirtualAgent.selectNumber')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "number",
              modelValue: _unref(v$).number.$model,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).number.$model) = $event)),
              options: numberOptions.value,
              errors: _unref(v$).number.$silentErrors,
              label: "number",
              "value-prop": "id",
              "can-clear": ""
            }, null, 8, ["modelValue", "options", "errors"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_UiButton, {
              variant: "white",
              "text-variant": "gray-700",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showForm.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_UiButton, {
              type: "submit",
              variant: "primary",
              "text-variant": "white"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Apply')), 1)
              ]),
              _: 1
            })
          ])
        ], 32)
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ], 64))
}
}

})